import { useState, useEffect } from 'react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import {
  Mail,
  Plus,
  Loader2,
  CheckCircle,
  Clock,
  AlertTriangle,
  Send,
  ChevronDown,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import {
  getSpamUploadRequests,
  createSpamUploadRequest,
  sendSpamUploadEmail,
  getSpamUploadDetail,
  SpamUploadRequestData,
  getClients,
  getClient,
} from '../../services/api';
import SpamAnalysisDetail from './SpamAnalysisDetail';

interface ClientContact {
  name: string;
  email: string;
  phone?: string;
}

const UPLOAD_BASE_URL = 'https://pax8lic-api.azurewebsites.net/api';

interface SpamSamplesPanelProps {
  clientId?: string;
  clientName?: string;
}

export default function SpamSamplesPanel({ clientId, clientName }: SpamSamplesPanelProps = {}) {
  const [requests, setRequests] = useState<SpamUploadRequestData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedData, setExpandedData] = useState<SpamUploadRequestData | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Create form state - pre-fill client when props provided
  const [clientSearch, setClientSearch] = useState(clientName || '');
  const [clientResults, setClientResults] = useState<{ id: string; companyName: string }[]>([]);
  const [selectedClient, setSelectedClient] = useState<{ id: string; companyName: string } | null>(
    clientId && clientName ? { id: clientId, companyName: clientName } : null
  );
  const [clientContacts, setClientContacts] = useState<ClientContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContactIndex, setSelectedContactIndex] = useState<string>('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [searchingClients, setSearchingClients] = useState(false);

  useEffect(() => { loadRequests(); }, [clientId]);

  // Load contacts when client is pre-selected via props
  useEffect(() => {
    if (clientId && clientName) {
      setLoadingContacts(true);
      getClient(clientId).then((fullClient: any) => {
        setClientContacts(fullClient.contacts || []);
      }).catch(() => {
        setClientContacts([]);
      }).finally(() => {
        setLoadingContacts(false);
      });
    }
  }, [clientId, clientName]);

  async function loadRequests() {
    try {
      const data = await getSpamUploadRequests(clientId);
      setRequests(data.items || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }

  async function searchClients(search: string) {
    setClientSearch(search);
    if (search.length < 2) { setClientResults([]); return; }
    setSearchingClients(true);
    try {
      const data = await getClients({ search, pageSize: 10 });
      setClientResults(data.items.map(c => ({ id: c.id, companyName: c.companyName })));
    } catch {
      setClientResults([]);
    } finally {
      setSearchingClients(false);
    }
  }

  async function selectClient(client: { id: string; companyName: string }) {
    setSelectedClient(client);
    setClientSearch(client.companyName);
    setClientResults([]);
    setSelectedContactIndex('');
    setContactName('');
    setContactEmail('');
    setClientContacts([]);
    setLoadingContacts(true);
    try {
      const fullClient = await getClient(client.id);
      setClientContacts((fullClient as any).contacts || []);
    } catch {
      setClientContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  }

  function handleContactSelect(value: string) {
    setSelectedContactIndex(value);
    if (value === 'other' || value === '') {
      setContactName('');
      setContactEmail('');
    } else {
      const idx = parseInt(value, 10);
      const contact = clientContacts[idx];
      if (contact) {
        setContactName(contact.name);
        setContactEmail(contact.email);
      }
    }
  }

  async function handleCreate() {
    if (!selectedClient || !contactEmail || !contactName) return;
    setCreating(true);
    setCreateError(null);
    setCreateSuccess(null);

    try {
      // Step 1: Create the upload request
      const result = await createSpamUploadRequest({
        clientId: selectedClient.id,
        clientName: selectedClient.companyName,
        contactEmail,
        contactName,
      });

      if (!result.success) throw new Error('Failed to create request');

      const uploadLink = `${UPLOAD_BASE_URL}/upload/${result.token}`;

      // Step 2: Send the email
      await sendSpamUploadEmail({
        recipientEmail: contactEmail,
        contactName,
        clientName: selectedClient.companyName,
        uploadLink,
        expiresAt: result.expiresAt,
      });

      setCreateSuccess(`Upload link sent to ${contactEmail}`);
      setShowCreate(false);
      resetForm();
      loadRequests();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create request');
    } finally {
      setCreating(false);
    }
  }

  function resetForm() {
    setSelectedClient(clientId && clientName ? { id: clientId, companyName: clientName } : null);
    setClientSearch(clientName || '');
    setContactEmail('');
    setContactName('');
    setSelectedContactIndex('');
    setClientContacts([]);
    setCreateError(null);
  }

  async function toggleExpand(req: SpamUploadRequestData) {
    if (expandedId === req.id) {
      setExpandedId(null);
      setExpandedData(null);
      return;
    }
    setExpandedId(req.id);
    if (req.analysis) {
      setExpandedData(req);
      return;
    }
    setLoadingDetail(true);
    try {
      const data = await getSpamUploadDetail(req.id);
      setExpandedData(data.request);
    } catch {
      setExpandedData(null);
    } finally {
      setLoadingDetail(false);
    }
  }

  const statusConfig = {
    pending: { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/10', label: 'Pending' },
    uploaded: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-500/10', label: 'Uploaded' },
    expired: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/10', label: 'Expired' },
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-red-500" />
          <h3 className="text-lg font-semibold text-foreground">Spam Sample Requests</h3>
        </div>
        <button
          onClick={() => { setShowCreate(!showCreate); resetForm(); setCreateSuccess(null); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Request
        </button>
      </div>

      {/* Success toast */}
      {createSuccess && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 flex items-center gap-2">
          <Send className="h-4 w-4 text-emerald-500" />
          <span className="text-sm text-emerald-600 dark:text-emerald-400">{createSuccess}</span>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div className="border border-border rounded-xl bg-card p-4 space-y-3">
          <h4 className="text-sm font-medium text-foreground">Request Spam Samples</h4>

          {/* Client search - hidden when client is pre-set via props */}
          {clientId && clientName ? (
            <div>
              <label className="text-xs text-muted-foreground">Client</label>
              <div className="mt-1 px-3 py-2 text-sm rounded-lg border border-border bg-muted text-foreground">
                {clientName}
              </div>
            </div>
          ) : (
            <div className="relative">
              <label className="text-xs text-muted-foreground">Client</label>
              <input
                type="text"
                value={clientSearch}
                onChange={e => searchClients(e.target.value)}
                placeholder="Search for a client..."
                className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground"
              />
              {searchingClients && <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-8 text-muted-foreground" />}
              {clientResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {clientResults.map(c => (
                    <button key={c.id} onClick={() => selectClient(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-accent">
                      {c.companyName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Contact picker */}
          {selectedClient && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Contact</label>
                {loadingContacts ? (
                  <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading contacts...
                  </div>
                ) : (
                  <select
                    value={selectedContactIndex}
                    onChange={e => handleContactSelect(e.target.value)}
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground"
                  >
                    <option value="">Select a contact...</option>
                    {clientContacts.map((c, i) => (
                      <option key={i} value={i.toString()}>{c.name} ({c.email})</option>
                    ))}
                    <option value="other">Other (enter manually)</option>
                  </select>
                )}
              </div>
              {selectedContactIndex === 'other' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Contact Name</label>
                    <input
                      type="text"
                      value={contactName}
                      onChange={e => setContactName(e.target.value)}
                      placeholder="Julie Tepetla"
                      className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Contact Email</label>
                    <input
                      type="email"
                      value={contactEmail}
                      onChange={e => setContactEmail(e.target.value)}
                      placeholder="julie@machnetworks.com"
                      className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground"
                    />
                  </div>
                </div>
              )}
              {selectedContactIndex !== '' && selectedContactIndex !== 'other' && contactName && (
                <div className="text-sm text-muted-foreground">
                  Sending to: <span className="font-medium text-foreground">{contactName}</span> &lt;{contactEmail}&gt;
                </div>
              )}
            </div>
          )}

          {createError && (
            <div className="text-sm text-red-500">{createError}</div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={!selectedClient || !contactEmail || !contactName || creating}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {creating ? 'Sending...' : 'Create & Send Email'}
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-border text-muted-foreground hover:bg-accent transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Request list */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          No spam sample requests yet. Click "New Request" to get started.
        </div>
      ) : (
        <div className="space-y-2">
          {requests.map(req => {
            const config = statusConfig[req.status];
            const StatusIcon = config.icon;
            const isExpanded = expandedId === req.id;

            return (
              <div key={req.id} className="border border-border rounded-xl bg-card overflow-hidden">
                <button
                  onClick={() => toggleExpand(req)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-accent/50 transition-colors text-left"
                >
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.color}`}>
                    <StatusIcon className="h-3 w-3" />
                    {config.label}
                  </span>
                  <span className="text-sm font-medium text-foreground">{req.clientName}</span>
                  <span className="text-xs text-muted-foreground">{req.contactName} ({req.contactEmail})</span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {formatDistanceToNow(parseISO(req.createdAt), { addSuffix: true })}
                  </span>
                  {req.fileCount && (
                    <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">{req.fileCount} file{req.fileCount > 1 ? 's' : ''}</span>
                  )}
                </button>

                {isExpanded && (
                  <div className="border-t border-border px-4 py-4 space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <div>Created: {format(parseISO(req.createdAt), 'MMM d, yyyy h:mm a')}</div>
                      <div>By: {req.createdByName}</div>
                      <div>Expires: {format(parseISO(req.expiresAt), 'MMM d, yyyy h:mm a')}</div>
                      {req.uploadedAt && <div>Uploaded: {format(parseISO(req.uploadedAt), 'MMM d, yyyy h:mm a')}</div>}
                    </div>

                    {req.status === 'pending' && (
                      <div className="flex items-center gap-2">
                        <a
                          href={`${UPLOAD_BASE_URL}/upload/${req.token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Upload Link
                        </a>
                      </div>
                    )}

                    {loadingDetail && expandedId === req.id ? (
                      <div className="flex items-center gap-2 py-4">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Loading analysis...</span>
                      </div>
                    ) : expandedData?.analysis ? (
                      <SpamAnalysisDetail files={expandedData.analysis.files} />
                    ) : req.status === 'pending' ? (
                      <p className="text-sm text-muted-foreground">Waiting for client to upload samples.</p>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
